<template>
    <div class="admin-forum-container">
        <div class="page-header">
            <h2>论坛内容管理</h2>
            <div class="header-actions">
                <el-input v-model="searchQuery" placeholder="搜索标题或内容..." class="search-input" clearable
                    @clear="fetchPosts" @keyup.enter="fetchPosts">
                    <template #append>
                        <el-button icon="Search" @click="fetchPosts" />
                    </template>
                </el-input>
            </div>
        </div>

        <div class="filter-bar">
            <el-radio-group v-model="filterType" @change="fetchPosts">
                <el-radio-button label="">全部类型</el-radio-button>
                <el-radio-button label="question">提问</el-radio-button>
                <el-radio-button label="article">分享</el-radio-button>
                <el-radio-button label="moment">新鲜事</el-radio-button>
            </el-radio-group>
            <el-radio-group v-model="filterStatus" @change="fetchPosts" style="margin-left: 20px;">
                <el-radio-button label="">全部状态</el-radio-button>
                <el-radio-button label="open">待解决/公开</el-radio-button>
                <el-radio-button label="resolved">已解决</el-radio-button>
                <el-radio-button label="archived">已归档</el-radio-button>
            </el-radio-group>
        </div>

        <el-table v-loading="loading" :data="posts" style="width: 100%" border stripe>
            <el-table-column label="ID" prop="id" width="70" />
            <el-table-column label="标题/内容" min-width="250">
                <template #default="{ row }">
                    <div class="post-info">
                        <el-tag size="small" :type="getTypeTag(row.type)" style="margin-right: 8px;">
                            {{ getTypeLabel(row.type) }}
                        </el-tag>
                        <span class="post-title" @click="viewPostDetail(row)">{{ row.title || '（新鲜事无标题）' }}</span>
                        <p class="post-excerpt">{{ getExcerpt(row.content) }}</p>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="作者" width="120">
                <template #default="{ row }">
                    <span>{{ row.author.username }}</span>
                </template>
            </el-table-column>
            <el-table-column label="发布时间" width="160">
                <template #default="{ row }">
                    {{ formatDate(row.createdAt) }}
                </template>
            </el-table-column>
            <el-table-column label="IP属地" width="100" prop="ipLocation" />
            <el-table-column label="状态" width="110">
                <template #default="{ row }">
                    <el-select v-model="row.status" size="small" @change="updatePost(row)">
                        <el-option label="待解决/公开" value="open" />
                        <el-option label="已解决" value="resolved" />
                        <el-option label="已归档" value="archived" />
                    </el-select>
                </template>
            </el-table-column>
            <el-table-column label="可见性" width="100">
                <template #default="{ row }">
                    <el-switch v-model="row.isHidden" :active-value="false" :inactive-value="true" active-text="显示"
                        inactive-text="隐藏" inline-prompt @change="toggleHidden(row)" />
                </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
                <template #default="{ row }">
                    <el-button type="primary" link @click="viewPostDetail(row)">详情/评论</el-button>
                    <el-popconfirm title="确定要删除这篇帖子吗？" @confirm="deletePost(row.id)">
                        <template #reference>
                            <el-button type="danger" link>删除</el-button>
                        </template>
                    </el-popconfirm>
                </template>
            </el-table-column>
        </el-table>

        <div class="pagination-container">
            <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :total="total"
                layout="total, prev, pager, next" @current-change="fetchPosts" />
        </div>

        <!-- Detail Dialog with Comment Moderation -->
        <el-dialog v-model="detailVisible" title="帖子详情及评论管理" width="800px">
            <div v-if="selectedPost" class="post-detail-view">
                <div class="detail-header">
                    <h3>{{ selectedPost.title }}</h3>
                    <div class="detail-meta">
                        作者: {{ selectedPost.author.username }} | 时间: {{ formatDate(selectedPost.createdAt) }} | IP: {{
                        selectedPost.ip }} ({{ selectedPost.ipLocation }})
                    </div>
                </div>
                <div class="detail-content">
                    <v-md-editor :model-value="selectedPost.content" mode="preview" />
                </div>

                <el-divider content-position="left">评论管理</el-divider>
                <el-table :data="selectedPostComments" size="small" border stripe max-height="400">
                    <el-table-column label="用户" prop="author.username" width="100" />
                    <el-table-column label="内容" prop="content" min-width="200" />
                    <el-table-column label="时间" width="140">
                        <template #default="{ row }">
                            {{ formatDate(row.createdAt) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="可见性" width="90">
                        <template #default="{ row }">
                            <el-switch v-model="row.isHidden" size="small" @change="toggleCommentHidden(row)" />
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/utils/api'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'

const loading = ref(false)
const posts = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)
const searchQuery = ref('')
const filterType = ref('')
const filterStatus = ref('')

const detailVisible = ref(false)
const selectedPost = ref(null)
const selectedPostComments = ref([])

const fetchPosts = async () => {
    loading.value = true
    try {
        const params = {
            page: currentPage.value,
            page_size: pageSize.value,
            type: filterType.value,
            status: filterStatus.value,
            keyword: searchQuery.value
        }
        const response = await api.get('/api/admin/forum/posts', { params })
        posts.value = response.data.posts
        total.value = response.data.total
    } catch (error) {
        ElMessage.error('获取帖子列表失败')
    } finally {
        loading.value = false
    }
}

const updatePost = async (row) => {
    try {
        await api.put(`/api/admin/forum/posts/${row.id}`, {
            status: row.status,
            isHidden: row.isHidden
        })
        ElMessage.success('更新成功')
    } catch (error) {
        ElMessage.error('更新失败')
        fetchPosts()
    }
}

const toggleHidden = async (row) => {
    updatePost(row)
}

const deletePost = async (id) => {
    try {
        await api.delete(`/api/forum/posts/${id}`) // Reusing existing delete
        ElMessage.success('删除成功')
        fetchPosts()
    } catch (error) {
        ElMessage.error('删除失败')
    }
}

const viewPostDetail = async (post) => {
    selectedPost.value = post
    try {
        const response = await api.get(`/api/forum/posts/${post.id}`)
        selectedPostComments.value = response.data.comments
        detailVisible.value = true
    } catch (error) {
        ElMessage.error('获取详情失败')
    }
}

const toggleCommentHidden = async (comment) => {
    try {
        await api.put(`/api/admin/forum/comments/${comment.id}`, {
            isHidden: comment.isHidden
        })
        ElMessage.success('评论状态已更新')
    } catch (error) {
        ElMessage.error('更新失败')
    }
}

const getTypeTag = (type) => {
    const map = {
        'question': 'warning',
        'article': 'success',
        'moment': 'primary'
    }
    return map[type] || 'info'
}

const getTypeLabel = (type) => {
    const map = {
        'question': '提问',
        'article': '分享',
        'moment': '新鲜事'
    }
    return map[type] || '其他'
}

const getExcerpt = (content) => {
    if (!content) return ''
    return content.length > 50 ? content.substring(0, 50) + '...' : content
}

const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD HH:mm')
}

onMounted(() => {
    fetchPosts()
})
</script>

<style scoped>
.admin-forum-container {
    padding: 20px;
    background: #fff;
    border-radius: 8px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.search-input {
    width: 300px;
}

.filter-bar {
    margin-bottom: 20px;
}

.post-title {
    font-weight: bold;
    cursor: pointer;
    color: #409eff;
}

.post-excerpt {
    color: #909399;
    font-size: 12px;
    margin-top: 4px;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.detail-header {
    margin-bottom: 20px;
}

.detail-meta {
    color: #909399;
    font-size: 13px;
    margin-top: 8px;
}

.detail-content {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 15px;
    margin-bottom: 20px;
    max-height: 300px;
    overflow-y: auto;
}
</style>
